/* eslint-disable no-unused-vars */
import {
  CSSProperties,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridCellParams,
  gridClasses,
  GridSortModel,
  GridSortItem,
  GridRowParams,
  GridRowIdGetter,
} from '@mui/x-data-grid';
import { CircularProgress, Stack } from '@mui/material';
import AgLoadingCard from '../AgLoadingCard';
import { useIntl } from 'react-intl';
import AgModal from '../AgModal';
import AgButton from '../Components/AgButton';
import { AgColor } from '../Styles/AgColor';
import AgTableToolbar from '../AgTableToolbar';
import AgTypography, { styleMappings } from '../Styles/AgTypography';
import CustomNoRowsOverlay from './NoData';
import AgFancyDropdown, {
  DropdownOptionProps,
} from '../Components/CustomDropdowns/AgFancyDropdown';
import { FilterSelectorProps } from '../Components/CustomDropdowns/AgFilterSelector';
import { SERVER_RENDERING_THRESHOLD } from '../../Constants';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { MenuItemData } from '../AgMenuItemData';
import CustomTable from './CustomTable/CustomTable';

export default function AgTable({
  apiRef,
  initialRows,
  columns,
  editable,
  type,
  pinned,
  showToolbar,
  setSort,
  newMenus,
  menuComponents,
  selectedIds, // if falsy, the checkbox selection is uncontrolled
  setSelectedIDs,
  setSelectedRowIndex,
  showAddNewButtonIcon = true,
  addNewbuttonName,
  handleAddNewButton,
  hasAddNewButton = true,
  addNewButtonDisabled,
  addNewHide,
  buttonLabel,
  tableLoading,
  hasMultipleRoles,
  rolesOptions,
  rolesOptions2,
  buttonLabel2,
  buttonIcon2,
  totals,
  tableToolBarLeftElementFilter,
  filteredNoResult,
  filterSelector,
  disabledCheckbox,
  noRowsOverlayTitle,
  noRowsOverlayDesc,
  noRowsOverlayContent,
  allowToSelectBoolean,
  showTableMenu,
  disabledHighlight,
  checkboxDisabled,
  keepNonExistentRowsSelected,
  disableRowSelectionOnClick,
  disableRowSelectionOnCheck,
  getRowId,
  customElement,
  loadingLabel,
  bottomLeftElement,
  renderExpandedContent,
  isExpandable,
  hideFooterPagination,
  containerHeight,
  maxContainerHeight,
  disableExpandOn,
  singleSelect,
  noShadow,
}: {
  apiRef?: React.MutableRefObject<GridApiCommunity>;
  initialRows: GridRowsProp;
  columns: GridColDef[];
  editable?: boolean;
  type?:
    | 'members'
    | 'staff'
    | 'organizations'
    | 'certifications'
    | 'generic'
    | 'default';
  pinned?: boolean;
  showToolbar?: boolean;
  setSort?: (obj: GridSortItem) => void;
  newMenus?: MenuItemData;
  menuComponents?: ReactNode[];
  selectedIds?: number[];
  setSelectedIDs?: (values: number[]) => void;
  setSelectedRowIndex?: Function | Dispatch<SetStateAction<number | undefined>>;
  showAddNewButtonIcon?: boolean;
  addNewbuttonName?: string;
  handleAddNewButton?: () => void;
  hasAddNewButton?: boolean;
  addNewButtonDisabled?: boolean;
  addNewHide?: boolean;
  buttonLabel?: string;
  tableLoading?: boolean;
  hasMultipleRoles?: boolean;
  rolesOptions?: DropdownOptionProps[];
  rolesOptions2?: DropdownOptionProps[];
  totals?: boolean;
  buttonLabel2?: string;
  buttonIcon2?: ReactElement;
  tableToolBarLeftElementFilter?: ReactNode;
  filteredNoResult?: boolean; // is true if there is filter condition but no result
  filterSelector?: FilterSelectorProps[];
  disabledCheckbox?: boolean;
  noRowsOverlayTitle?: string;
  noRowsOverlayDesc?: string;
  noRowsOverlayContent?: ReactNode;
  allowToSelectBoolean?: boolean;
  showTableMenu?: boolean;
  disabledHighlight?: boolean;
  checkboxDisabled?: boolean;
  keepNonExistentRowsSelected?: boolean;
  disableRowSelectionOnClick?: boolean;
  disableRowSelectionOnCheck?: boolean;
  getRowId?: GridRowIdGetter<any>;
  selectionIdDefault?: boolean;
  customElement?: ReactNode;
  loadingLabel?: string;
  bottomLeftElement?: ReactNode;
  renderExpandedContent?: (row: any) => ReactNode;
  isExpandable?: boolean;
  hideFooterPagination?: boolean;
  containerHeight?: string;
  maxContainerHeight?: string;
  disableExpandOn?: (row: any) => boolean;
  singleSelect?: boolean;
  noShadow?: boolean;
}) {
  const messages = useIntl().formatMessage;
  const [rows, setRows] = useState(initialRows);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [openNameModal, setOpenNameModal] = useState<boolean>(false);
  const [rowsCount, setRowsCount] = useState<number>(
    initialRows?.length < 100 ? initialRows?.length : 100
  );
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [queryOptions, setQueryOptions] = useState<GridSortItem>();
  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setQueryOptions(sortModel[0]);
  }, []);

  useEffect(() => {
    if (queryOptions && setSort) setSort(queryOptions);
  }, [queryOptions, setSort]);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  useEffect(() => {
    setRowsCount(initialRows?.length < 100 ? initialRows?.length : 100);
  }, [initialRows?.length]);

  const disabledEditRow = addNewButtonDisabled && type === 'staff';

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const highLightHeaderColumnColor = (
    columns: GridColDef[],
    filterSelector?: FilterSelectorProps[]
  ) => {
    // task 5105, update headers and cells UI when filter selector is active
    if (!filterSelector || !filterSelector.length) return;
    // comparing strings, because the field doesn't have a stable id from API response, and may have localization problem, may need to be changed in the future
    const headerNames = columns.map((item) => item?.headerName);
    const filterHeaders = filterSelector?.reduce(
      (styles: Record<string, CSSProperties>, filter) => {
        const hasFilter = headerNames.some((item?: string) =>
          (item || '').startsWith(filter.filter)
        );
        const filterName = filter.filter.replaceAll(' ', '-');
        if (hasFilter) {
          styles[`& .header-${filterName}`] = {
            backgroundColor: AgColor.secondaryLight2,
          };
          styles[`& .cell-${filterName}`] = {
            backgroundColor: AgColor.grey1,
          };
        }
        return styles;
      },
      {}
    );
    return filterHeaders || {};
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const filteredNoResultInfo = () => {
    // if there is filter condition but no result, show message here
    return (
      <AgTypography
        style={{
          width: 'auto',
          height: '100%',
          justifyContent: 'center',
          display: 'flex',
          alignItems: 'center',
        }}
        contentStyle={'AP.Paragraph.Large.Regular'}
      >
        {messages({ id: 'FILTERED_NO_DATA' })}
      </AgTypography>
    );
  };

  const NoRowsOverlay = () => (
    <CustomNoRowsOverlay
      title={noRowsOverlayTitle}
      description={noRowsOverlayDesc}
      children={noRowsOverlayContent}
    />
  );

  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (disableRowSelectionOnCheck && params.field === '__check__') return;
      if (params.field === 'custom-link') return;
      const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

      if (params.field === 'name' && isInEditMode) setOpenNameModal(true);

      if (
        setSelectedRowIndex &&
        (typeof params.value !== 'boolean' || allowToSelectBoolean)
      )
        setSelectedRowIndex(parseInt(params.row.id) - 1);
    },
    [
      allowToSelectBoolean,
      disableRowSelectionOnCheck,
      rowModesModel,
      setSelectedRowIndex,
    ]
  );

  const handleClickAddNewButton = useCallback(async () => {
    try {
      setIsBtnLoading(true);
      handleAddNewButton && (await handleAddNewButton());
    } finally {
      setIsBtnLoading(false);
    }
  }, [handleAddNewButton]);

  const actionColumn = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 100,
    cellClassName: 'actions',
    getActions: ({ id }: { id: string }) => {
      const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <GridActionsCellItem
            icon={<SaveIcon />}
            label='Save'
            sx={{
              color: 'primary.main',
            }}
            onClick={handleSaveClick(id)}
          />,
          <GridActionsCellItem
            icon={<CancelIcon />}
            label='Cancel'
            className='textPrimary'
            onClick={handleCancelClick(id)}
            color='inherit'
          />,
        ];
      }

      return [
        <GridActionsCellItem
          icon={<EditIcon />}
          label='Edit'
          className='textPrimary'
          onClick={handleEditClick(id)}
          color='inherit'
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label='Delete'
          onClick={handleDeleteClick(id)}
          color='inherit'
        />,
      ];
    },
  };

  const rowHighlight = disabledHighlight
    ? 'transparent'
    : AgColor.secondaryLight2;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        marginTop: '-16px',
        backgroundColor: AgColor.grey0,
        borderRadius: '8px',
        boxShadow: noShadow ? 'none' : '0px 1px 2px 0px #0000000D',
      }}
    >
      {showToolbar && (
        <AgTableToolbar
          type={type}
          initialRows={initialRows}
          rowsCount={rowsCount}
          showTableMenu={showTableMenu}
          addNew={
            hasAddNewButton ? (
              hasMultipleRoles ? (
                <Stack direction='row' gap={2}>
                  {(rolesOptions2 || []).length > 0 && (
                    <AgFancyDropdown
                      buttonLabel={messages({ id: buttonLabel2 })}
                      buttonStartIcon={
                        buttonIcon2 || (
                          <Add sx={{ width: '16px', height: '16px' }} />
                        )
                      }
                      style={{
                        padding: '4px 15px',
                        fontSize: '10px',
                        minWidth: '105px',
                        maxWidth: '200px',
                      }}
                      buttonDisabled={addNewButtonDisabled}
                      options={rolesOptions2 || []}
                    />
                  )}
                  {addNewHide ? null : (
                    <AgFancyDropdown
                      buttonLabel={messages({
                        id: buttonLabel || 'MEMBER_ADD_NEW',
                      })}
                      buttonStartIcon={
                        <Add sx={{ width: '16px', height: '16px' }} />
                      }
                      style={{
                        padding: '4px 15px',
                        fontSize: '10px',
                        minWidth: '105px',
                        maxWidth: '200px',
                      }}
                      buttonDisabled={addNewButtonDisabled}
                      options={rolesOptions || []}
                    />
                  )}
                </Stack>
              ) : (
                <Stack direction='row' gap={2}>
                  <AgButton
                    label={messages({
                      id: addNewbuttonName || 'MEMBER_ADD_NEW',
                    })}
                    startIcon={
                      showAddNewButtonIcon || isBtnLoading ? (
                        isBtnLoading ? (
                          <CircularProgress color='inherit' size={13} />
                        ) : (
                          <Add sx={{ width: '16px', height: '16px' }} />
                        )
                      ) : null
                    }
                    style={{
                      padding: '4px 15px',
                      fontSize: '10px',
                      minWidth: '105px',
                      maxWidth: '200px',
                    }}
                    onClick={handleClickAddNewButton}
                    disabled={addNewButtonDisabled || isBtnLoading}
                  />
                  {customElement}
                </Stack>
              )
            ) : null
          }
          newMenus={newMenus}
          menuComponents={menuComponents}
          leftElementFilter={tableToolBarLeftElementFilter}
        />
      )}
      {!columns || columns.length <= 0 || !rows || tableLoading ? (
        <AgLoadingCard
          text={loadingLabel || messages({ id: 'LOADING' })}
          style={{
            position: 'relative',
            padding: '20px',
            height: containerHeight ?? '74vh',
          }}
        />
      ) : isExpandable ? (
        <CustomTable
          singleSelect={singleSelect}
          disableExpandOn={disableExpandOn}
          rows={rows as any[]}
          columns={editable ? [...columns, ...[actionColumn]] : columns}
          selectedIds={selectedIds as any}
          setSelectedIDs={
            checkboxDisabled ? undefined : (setSelectedIDs as any)
          }
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          getRowId={getRowId}
          allowToSelectBoolean={allowToSelectBoolean}
          setSelectedRowIndex={setSelectedRowIndex as any}
          stickyHeaderOffset={showToolbar ? 48 : 0}
          renderExpandedContent={renderExpandedContent as any}
          onPageInfoChange={({ page, rowsPerPage, total }) => {
            const count = Math.min((page + 1) * rowsPerPage, total);
            setRowsCount(count);
          }}
          slots={{
            noRowsOverlay: filteredNoResult
              ? filteredNoResultInfo
              : NoRowsOverlay,
          }}
        />
      ) : (
        <DataGrid
          apiRef={apiRef}
          slots={{
            noRowsOverlay: filteredNoResult // if filter selector is used, then showing another message as per Figma
              ? filteredNoResultInfo
              : NoRowsOverlay,
          }}
          checkboxSelection={!disabledEditRow && !disabledCheckbox}
          isRowSelectable={(params: GridRowParams) =>
            !Object.keys(params.row).some((key) =>
              /^total$/gi.test(params.row[key])
            )
          } // Disabled selection of total row
          getRowClassName={(params) =>
            Object.keys(params.row).some((key) =>
              /^total$/gi.test(params.row[key])
            )
              ? 'total-wrapper'
              : ''
          } // Added custom class name for the total row
          rows={rows}
          columns={editable ? [...columns, ...[actionColumn]] : columns}
          editMode='row'
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onCellClick={handleCellClick}
          sortingMode={
            rows.length > SERVER_RENDERING_THRESHOLD ? 'server' : 'client'
          }
          onSortModelChange={(sortModal) =>
            rows.length > SERVER_RENDERING_THRESHOLD &&
            handleSortModelChange(sortModal)
          }
          disableColumnMenu
          disableColumnSelector
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          sx={{
            [`.${gridClasses.main}`]: {
              ...styleMappings['AP.Paragraph.Small.Regular'],
              overflow: 'unset',
              height: containerHeight ?? '73vh',
              maxHeight: maxContainerHeight ?? 'unset',
            },
            [`.${gridClasses.columnHeaders}`]: {
              ...styleMappings['AP.H5.Bold'],
              color: AgColor.grey9,
              position: 'sticky',
              top: showToolbar ? 48 : 0,
              backgroundColor: AgColor.grey0,
              zIndex: 999,
            },
            [`.${gridClasses.footerContainer}`]: {
              display: hideFooterPagination ? 'none' : 'flex',
            },
            ...highLightHeaderColumnColor(columns, filterSelector),
            '&.MuiDataGrid-root': {
              borderStyle: 'unset',
            },
            '& .MuiDataGrid-cell': {
              opacity: 1,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: AgColor.grey3,
              '& .MuiDataGrid-cell:nth-of-type(2)': {
                backgroundColor: AgColor.grey3,
              },
              '& .MuiDataGrid-cell:first-of-type': {
                backgroundColor: AgColor.grey3,
              },
            },
            '.MuiDataGrid-selectedRowCount': {
              visibility: 'hidden',
            },
            '& .MuiDataGrid-iconSeparator': {
              visibility: 'hidden',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              lineHeight: '20px',
              whiteSpace: 'break-spaces',
            },
            // do not delete: could be used for the total footer
            ...(totals
              ? {
                  '& .total-wrapper': {
                    backgroundColor: totals ? AgColor.grey3 : 'transparent',
                    position: 'sticky',
                    left: 0,
                    bottom: 0,
                    zIndex: 99999,

                    '& .MuiDataGrid-cell:first-of-type': {
                      backgroundColor: totals ? AgColor.grey3 : 'transparent',
                      position: 'initial',

                      '& .MuiCheckbox-root': {
                        display: totals ? 'none' : 'flex',
                      },
                    },
                    '& .MuiDataGrid-cell:nth-of-type(2)': {
                      backgroundColor: totals ? AgColor.grey3 : 'transparent',
                      position: 'initial',
                    },
                  },
                }
              : {}),
            '& .MuiDataGrid-row': {
              ...(disabledEditRow
                ? {
                    pointerEvents: 'none',
                    cursor: 'not-allowed',
                  }
                : {}),
              '&.Mui-selected': {
                backgroundColor: rowHighlight,
                '& .MuiDataGrid-cell:nth-of-type(2)': {
                  backgroundColor: rowHighlight,
                },
                '& .MuiDataGrid-cell:first-of-type': {
                  backgroundColor: rowHighlight,
                },
                '&:hover': {
                  backgroundColor: AgColor.secondaryLight2,
                  '& .MuiDataGrid-cell:nth-of-type(2)': {
                    backgroundColor: AgColor.secondaryLight2,
                  },
                  '& .MuiDataGrid-cell:first-of-type': {
                    backgroundColor: AgColor.secondaryLight2,
                  },
                },
              },
            },
            '.MuiDataGrid-virtualScroller': {
              backgroundColor: AgColor.grey0,
              overflow: 'auto',
              marginTop: '0px !important',
              minHeight: 48,
            },
            '& .MuiDataGrid-cell:first-of-type': {
              backgroundColor: AgColor.grey0,
              position: pinned ? 'sticky' : 'inherit',
              top: showToolbar ? 48 : 0,
              left: '0',
              zIndex: 999,
            },
            '& .MuiDataGrid-cell:nth-of-type(2)': {
              backgroundColor: AgColor.grey0,
              position: pinned ? 'sticky' : 'inherit',
              top: showToolbar ? 48 : 0,
              left: '50px !important',
              borderRight: `.25px solid ${AgColor.grey4}`,
              zIndex: 999,
            },
            '& .MuiDataGrid-cell:nth-of-type(8)': {
              // height: '200px'
            },
            '& .MuiDataGrid-columnHeader:first-of-type': {
              backgroundColor: AgColor.grey0,
              position: pinned ? 'sticky' : 'inherit',
              top: showToolbar ? 48 : 0,
              left: '0px !important',
              zIndex: 999,
            },
            '& .MuiDataGrid-columnHeader:nth-of-type(2)': {
              backgroundColor: AgColor.grey0,
              position: pinned ? 'sticky' : 'inherit',
              top: showToolbar ? 48 : 0,
              left: '50px !important',
              borderRight: `.25px solid ${AgColor.grey4}`,
              zIndex: 999,
            },
            '.css-ptiqhd-MuiSvgIcon-root': {
              color: AgColor.secondaryDark,
            },
            '.css-gl260s-MuiDataGrid-columnHeadersInner': {
              // overflow: 'auto'
            },
          }}
          rowSelectionModel={selectedIds || undefined}
          onRowSelectionModelChange={(ids) => {
            if (checkboxDisabled) return;
            if (getRowId && setSelectedIDs) {
              setSelectedIDs(ids as any);
              return;
            }

            const numbersArray: number[] = new Array(ids)
              .flat()
              .map((item) =>
                typeof item === 'number' ? item : parseInt(item as string, 10)
              );

            rows.filter((row) => numbersArray.includes(parseInt(row.id)));

            setSelectedIDs && setSelectedIDs(numbersArray);
          }}
          onPaginationModelChange={(details) => {
            const count = (details.page + 1) * details.pageSize;
            setRowsCount(count);
          }}
          disableColumnFilter
          onCellDoubleClick={(params, event) => {
            if (!editable) {
              event.defaultMuiPrevented = true;
            }
          }}
          keepNonExistentRowsSelected={keepNonExistentRowsSelected}
          getRowId={getRowId}
          hideFooterPagination={hideFooterPagination}
        />
      )}
      <AgModal
        open={openNameModal}
        footer={
          <AgButton
            onClick={() => setOpenNameModal(false)}
            label={messages({ id: 'CANCEL' })}
          />
        }
      />

      {bottomLeftElement}
    </Box>
  );
}
